import { Add as AddIcon } from '@mui/icons-material';
import HelpIcon from '@mui/icons-material/Help';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { InputProps } from '@mui/material/Input';
import { red } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { TFunction } from 'i18next';
import * as React from 'react';
import { FC, useEffect, useMemo, useState } from 'react';
import { ValidatorForm } from 'react-form-validator-core';
import { useTranslation } from 'react-i18next';

import { DiscreteStatesValidator } from '@/components/validators-forms/discrete-states-validator';
import InputValidator, { SelectValidator } from '@/components/validators-forms/input-validator';
import {
  convertMultiplierOffset,
  convertSensorMinMax,
  convertSensorMinMaxAndMultiplier,
  parseAnalogRange,
  recoverMinMaxWithMultiplier,
} from '@/lib/config-helpers';
import { SyncBadge } from '@/views/administration/device/config/device-config-card';
import WrapRequiredFieldsDescriptionComponent from '@/views/common-components/wrap-required-fileds-description';
import { SensorSettingsState, UpdateConfigInput } from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { TabPanelComponent, TabPropsType } from '.';

const isShown = (state: SensorSettingsState): boolean => {
  return (
    state.peripheralType === 'SENSOR' &&
    state.config?.sensor?.type !== Schema.SensorType.EVENT &&
    state.config?.sensor?.type !== Schema.SensorType.MANUAL_PROCESS
  );
};

const hasOnState = (state: SensorSettingsState): boolean => {
  return (state?.config?.sensor?.discreteConfig?.onStates?.length ?? 0) > 0;
};

const hasOffState = (state: SensorSettingsState): boolean => {
  return (state?.config?.sensor?.discreteConfig?.offStates?.length ?? 0) > 0;
};

const hasDiscreteStates = (state: SensorSettingsState): boolean => {
  return hasOnState(state) && hasOffState(state);
};

const isValid = (state: SensorSettingsState): boolean => {
  const { type, analogInputRange, dataMultiplier, analogOffset } = state.config?.sensor ?? {};
  switch (type) {
    case Schema.SensorType.MEASUREMENT:
      const useAnalogInputRange = !!analogInputRange;
      const useDataMultiplier =
        !analogInputRange && typeof dataMultiplier === 'number' && typeof analogOffset === 'number';
      return (!useAnalogInputRange && useDataMultiplier) || (useAnalogInputRange && !useDataMultiplier);
    // TS does not have XOR :(
    case Schema.SensorType.DISCRETE:
      return hasDiscreteStates(state) && typeof dataMultiplier === 'number' && dataMultiplier > 0;
    default:
      return typeof dataMultiplier === 'number' && dataMultiplier > 0;
  }
};

const AnalogInputExplanations: Record<Schema.AnalogInputRange, [number, number, 'V' | 'mA']> = {
  [Schema.AnalogInputRange.NEGATIVE_TEN_TO_TEN_VOLTS]: [-10, 10, 'V'],
  [Schema.AnalogInputRange.NEGATIVE_FIVE_TO_FIVE_VOLTS]: [-5, 5, 'V'],
  [Schema.AnalogInputRange.ZERO_TO_FIVE_VOLTS]: [0, 5, 'V'],
  [Schema.AnalogInputRange.ZERO_TO_TEN_VOLTS]: [0, 10, 'V'],
  [Schema.AnalogInputRange.NEGATIVE_TWENTYFIVE_TO_TWENTYFIVE_MILLIAMPS]: [-25, 25, 'mA'],
  [Schema.AnalogInputRange.ZERO_TO_TWENTY_MILLIAMPS]: [0, 20, 'mA'],
  [Schema.AnalogInputRange.ZERO_TO_TWENTYFOUR_MILLIAMPS]: [0, 24, 'mA'],
  [Schema.AnalogInputRange.FOUR_TO_TWENTY_MILLIAMPS]: [4, 20, 'mA'],
};

const translateAnalogExplanations = (t: TFunction) =>
  Object.entries(AnalogInputExplanations).map(([key, [min, max, unit]]) => ({
    key,
    name: `${min} ${t('shared:to', { defaultValue: 'to' })} ${max} ${t(`unit:${unit.toLowerCase()}`, {
      defaultValue: unit,
    })}`,
  }));

export const TabProps: TabPropsType = {
  isShown,
  isValid,
  label: (t) => t('device:sensorSetup', { defaultValue: 'Sensor' }),
};

ValidatorForm.addValidationRule('notZero', (value) => {
  return value !== 0;
});

ValidatorForm.addValidationRule('notEmpty', (value) => {
  if (value instanceof Array) {
    return value.length > 0;
  }
  return false;
});

export const TabPanel: TabPanelComponent<{
  sensor: Partial<Schema.Sensor>;
}> = (props) => {
  if (!isShown(props.state)) {
    return null;
  }

  switch (props.state.config?.sensor?.type) {
    case Schema.SensorType.MEASUREMENT:
      return <MeasurementContent {...props} />;
    case Schema.SensorType.COUNTER_ACCUMULATE:
      return <CounterAccContent {...props} />;
    case Schema.SensorType.DISCRETE:
      return <DiscreteContent {...props} />;
    default:
      return <CounterContent {...props} />;
  }
};

type HtmlEventHandler = (event: React.KeyboardEvent<HTMLDivElement>) => void;

const getPressEnterHandler = (elementId: string, onPressEnter: (stateValue: number) => void): HtmlEventHandler => {
  const handleAdd = getAddHandler(elementId, onPressEnter);

  return (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!event || !event.key || event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    handleAdd();
  };
};

const getAddHandler = (elementId: string, onAdd: (stateValue: number) => void) => {
  return () => {
    const input = document.getElementById(elementId) as HTMLInputElement;
    const stateValue = parseInt(input.value, 10);
    if (!Number.isNaN(stateValue)) {
      onAdd(stateValue);
    }
    input.value = '';
  };
};

const wiringOptions = Object.keys(Schema.SensorWiring).sort();

const WiringSelection: FC<{
  wiring: Omit<Schema.SensorWiringState, '__typename'> | null;
  handleChange: (newConfig: Partial<UpdateConfigInput>, meta?: { name?: string; description?: string }) => void;
}> = ({ wiring, handleChange }) => {
  const [t] = useTranslation();
  if (!wiring) {
    return null;
  }

  const reportedWiring = wiring.reported || null;
  const desiredWiring = wiring.desired;
  const onWiringChange: InputProps['onChange'] = ({ target: { value } }) => {
    handleChange({
      sensor: {
        wiring: {
          reported: reportedWiring,
          desired: value as Schema.SensorWiring,
        },
      },
    });
  };

  return (
    <Grid item flexDirection="row" display="flex" alignItems="center">
      <SelectValidator
        name="type"
        label={t('device:sensorWiring', { defaultValue: 'The type of wiring for the attached sensor' })}
        value={desiredWiring}
        onChange={onWiringChange}
      >
        {wiringOptions.map((wiringType) => {
          return (
            <MenuItem key={wiringType} value={wiringType}>
              {wiringType}
            </MenuItem>
          );
        })}
      </SelectValidator>
      <SyncBadge pendingChanges={reportedWiring !== desiredWiring} />
    </Grid>
  );
};

const DiscreteContent: TabPanelComponent = ({ handleChange, state, invalidFormMessage, classes, t }) => {
  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  const discreteConfig = state.config?.sensor?.discreteConfig || ({} as Schema.DiscreteConfigInput);

  const handleAddOnState = (stateValue: number) => {
    const currentOnStates = discreteConfig.onStates ?? [];
    const currentOffStates = discreteConfig.offStates ?? [];

    if (!currentOnStates.includes(stateValue)) {
      const index = currentOffStates.indexOf(stateValue);
      if (index >= 0) {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              onStates: [...currentOnStates, stateValue],
              offStates: currentOffStates.filter((_, i) => index !== i), // remove matching state
            },
          },
        });
      } else {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              onStates: [...currentOnStates, stateValue],
            },
          },
        });
      }
    }
  };

  const handleAddOffState = (stateValue: number) => {
    const currentOnStates = state?.config?.sensor?.discreteConfig?.onStates ?? [];
    const currentOffStates = state?.config?.sensor?.discreteConfig?.offStates ?? [];
    if (!currentOffStates.includes(stateValue)) {
      const index = currentOnStates.indexOf(stateValue);
      if (index >= 0) {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              onStates: currentOnStates.filter((_, i) => index !== i), // remove matching state
              offStates: [...currentOffStates, stateValue],
            },
          },
        });
      } else {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              offStates: [...currentOffStates, stateValue],
            },
          },
        });
      }
    }
  };

  const handleDeleteState = (stateValue: number) => {
    const currentOnStates = state?.config?.sensor?.discreteConfig?.onStates ?? [];
    const currentOffStates = state?.config?.sensor?.discreteConfig?.offStates ?? [];

    const onStateIndex = currentOnStates.indexOf(stateValue);
    const offStateIndex = currentOffStates.indexOf(stateValue);

    if (onStateIndex >= 0) {
      handleChange({
        sensor: {
          discreteConfig: {
            ...discreteConfig,
            onStates: currentOnStates.filter((_, i) => onStateIndex !== i), // remove matching state,
          },
        },
      });
    } else if (offStateIndex >= 0) {
      handleChange({
        sensor: {
          discreteConfig: {
            ...discreteConfig,
            offStates: currentOffStates.filter((_, i) => offStateIndex !== i), // remove matching state,
          },
        },
      });
    }
  };

  const onDefaultStateChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChange({
      sensor: {
        discreteConfig: {
          ...discreteConfig,
          defaultState: e.target.value as Schema.DiscreteDeviceState,
        },
      },
    });
  };

  return (
    <div className={classes.tabPanel}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClass}
            name="dataMultiplier"
            type="number"
            label={t('device:dataMultiplier', { defaultValue: 'Data multiplier' })}
            validators={['required', 'isPositive', 'notZero']}
            errorMessages={[
              t('shared:fieldIsRequired', { defaultValue: 'this field is required' }),
              t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
              t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
            ]}
            value={state?.config?.sensor?.dataMultiplier}
            helperText={
              !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0 ? (
                <span>
                  {t('device:configurationDataMultiplierExplanation', {
                    defaultValue:
                      'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
                    dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
                  })}
                </span>
              ) : undefined
            }
            onChange={onDataMultiplierChange}
          />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.discreteContainer}>
            <TextField
              variant="filled"
              id="onStatesInput"
              name="onStates"
              type="number"
              label={t('device:onStates', { defaultValue: 'ON States' })}
              style={{ flex: '1 1 auto' }}
              error={!hasOnState(state)}
              helperText={
                <span>
                  {t('device:discreteStatesOnExplanation', {
                    defaultValue:
                      'Specify the sensor states that correspond to the line being on (remember to click the + to add the state)',
                  })}
                </span>
              }
              onKeyPress={getPressEnterHandler('onStatesInput', handleAddOnState)}
            />

            <Button className={classes.discreteAddButton} onClick={getAddHandler('onStatesInput', handleAddOnState)}>
              <AddIcon />
            </Button>
          </div>

          <DiscreteStatesValidator
            validators={['notEmpty']}
            t={t}
            value={state?.config?.sensor?.discreteConfig?.onStates ?? []}
            onClickDelete={handleDeleteState}
          ></DiscreteStatesValidator>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.discreteContainer}>
            <TextField
              variant="filled"
              id="offStatesInput"
              name="offStates"
              type="number"
              label={t('device:offStates', { defaultValue: 'OFF States' })}
              style={{ flex: '1 1 auto' }}
              error={!hasOffState(state)}
              helperText={
                <span>
                  {t('device:discreteStatesOffExplanation', {
                    defaultValue:
                      'Specify the sensor states that correspond to the line being off (remember to click the + to add the state)',
                  })}
                </span>
              }
              onKeyPress={getPressEnterHandler('offStatesInput', handleAddOffState)}
            />

            <Button className={classes.discreteAddButton} onClick={getAddHandler('offStatesInput', handleAddOffState)}>
              <AddIcon />
            </Button>
          </div>
          <DiscreteStatesValidator
            validators={['notEmpty']}
            t={t}
            value={state?.config?.sensor?.discreteConfig?.offStates ?? []}
            onClickDelete={handleDeleteState}
          ></DiscreteStatesValidator>
        </Grid>

        <Grid item xs={12}>
          <SelectValidator
            className={classes.selectFields}
            containerClass={classes.containerClass}
            name="type"
            label={`${t('device:defaultState', { defaultValue: 'Default state' })} *`}
            helperText={
              <span>
                {t('device:discreteStatesDefaultExplanation', {
                  defaultValue:
                    'If the value is not caught by either of the above states, it will fallback to the default state',
                })}
              </span>
            }
            value={state?.config?.sensor?.discreteConfig?.defaultState ?? 'OFF'}
            validators={['required']}
            errorMessages={[t('shared:fieldIsRequired', { defaultValue: 'this field is required' })]}
            onChange={onDefaultStateChange}
          >
            <MenuItem key="off" value={'OFF'}>
              {t('shared:off', { defaultValue: 'Off' })}
            </MenuItem>
            <MenuItem key="on" value={'ON'}>
              {t('shared:on', { defaultValue: 'On' })}
            </MenuItem>
          </SelectValidator>
        </Grid>
      </Grid>
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
    </div>
  );
};

const CounterContent: TabPanelComponent = ({ handleChange, state, invalidFormMessage, classes, t }) => {
  ValidatorForm.addValidationRule('notZero', (value) => {
    return value !== 0;
  });
  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  return (
    <div className={classes.tabPanel}>
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="dataMultiplier"
        type="number"
        label={t('device:dataMultiplier', { defaultValue: 'Data multiplier' })}
        validators={['required', 'isPositive', 'notZero']}
        errorMessages={[
          t('shared:fieldIsRequired', { defaultValue: 'this field is required' }),
          t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
          t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
        ]}
        value={state?.config?.sensor?.dataMultiplier}
        helperText={
          !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0 ? (
            <span>
              {t('device:configurationDataMultiplierExplanation', {
                defaultValue:
                  'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
                dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
              })}
            </span>
          ) : undefined
        }
        onChange={onDataMultiplierChange}
      />
      {invalidFormMessage}
      <WiringSelection handleChange={handleChange} wiring={state.config?.sensor?.wiring || null} />
      <WrapRequiredFieldsDescriptionComponent />
      {/* Only counters get energy meter */}
      {state.config?.sensor?.type === Schema.SensorType.COUNTER ? (
        <>
          <div className={classes.containerClass}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={state.config?.sensor?.energyMeter ?? false}
                  onChange={(e) => {
                    if (e.target.checked && state.config?.sensor?.findStops) {
                      // Then we also disable findStops
                      handleChange({ sensor: { energyMeter: true, findStops: false } });
                    } else {
                      handleChange({ sensor: { energyMeter: e.target.checked } });
                    }
                  }}
                />
              }
              label={t('device:energyMeter', { defaultValue: 'Energy Meter' })}
              className={classes.textFields}
            />
          </div>
          <WrapRequiredFieldsDescriptionComponent
            overrideTitle={t('device:energyMeterDescription', {
              defaultValue: 'Enable if the sensor is an energy meter.',
            })}
          />
        </>
      ) : null}
    </div>
  );
};

ValidatorForm.addValidationRule('notZero', (value) => {
  return value !== 0;
});

const CounterAccContent: TabPanelComponent = ({ handleChange, state, invalidFormMessage, classes, t }) => {
  const accumulatorConfig = state.config?.sensor?.accumulatorConfig || ({} as Schema.AccumulatorConfigInput);
  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  const onRolloverValueChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        accumulatorConfig: {
          ...accumulatorConfig,
          rolloverValue: val.trim().length ? parseFloat(val) : undefined,
        },
      },
    });
  };

  const onPowerCheck = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange({
      sensor: {
        accumulatorConfig: { ...accumulatorConfig, resetsOnPowerOff: e.target.checked },
      },
    });

  const onRolloverThresholdChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        accumulatorConfig: {
          ...accumulatorConfig,
          rolloverThreshold: val.trim().length ? parseFloat(val) : undefined,
        },
      },
    });
  };

  return (
    <div className={classes.tabPanel}>
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="dataMultiplier"
        type="number"
        label={t('device:dataMultiplier', { defaultValue: 'Data multiplier' })}
        validators={['required', 'isPositive', 'notZero']}
        errorMessages={[
          t('shared:fieldIsRequired', { defaultValue: 'this field is required' }),
          t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
          t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
        ]}
        value={state?.config?.sensor?.dataMultiplier}
        helperText={
          !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0 ? (
            <span>
              {t('device:configurationDataMultiplierExplanation', {
                defaultValue:
                  'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
                dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
              })}
            </span>
          ) : undefined
        }
        onChange={onDataMultiplierChange}
      />
      <WiringSelection handleChange={handleChange} wiring={state.config?.sensor?.wiring || null} />
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="rolloverValue"
        type="number"
        label={t('device:rolloverValue', { defaultValue: 'Rollover value' })}
        validators={['required', 'isPositive']}
        errorMessages={[
          t('shared:fieldIsRequired', { defaultValue: 'this field is required' }),
          t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
          t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
        ]}
        value={state?.config?.sensor?.accumulatorConfig?.rolloverValue}
        onChange={onRolloverValueChange}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <FormControlLabel
          className={classes.checkboxField}
          control={
            <Checkbox
              checked={state?.config?.sensor?.accumulatorConfig?.resetsOnPowerOff ?? false}
              onChange={onPowerCheck}
              value="enabled"
              color="primary"
            />
          }
          label={`${t('device:counterCanReset', {
            defaultValue: 'Counter can reset (e.g. on power off or manually)',
          })}`}
        />
        {state?.config?.sensor?.accumulatorConfig?.resetsOnPowerOff ?? false ? (
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClassTwoColumns}
            style={{ marginRight: 10 }}
            name="rolloverThreshold"
            type="number"
            label={t('device:rolloverThreshold', { defaultValue: 'Rollover threshold' })}
            validators={['required', 'isPositive', 'notZero']}
            errorMessages={[
              t('shared:fieldIsRequired', { defaultValue: 'this field is required' }),
              t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
              t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
            ]}
            value={state?.config?.sensor?.accumulatorConfig?.rolloverThreshold}
            onChange={onRolloverThresholdChange}
          />
        ) : null}
      </div>
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
      <div className={classes.containerClass}>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={state.config?.sensor?.energyMeter ?? false}
              onChange={(e) => {
                if (e.target.checked && state.config?.sensor?.findStops) {
                  // Then we also disable findStops
                  handleChange({ sensor: { energyMeter: true, findStops: false } });
                } else {
                  handleChange({ sensor: { energyMeter: e.target.checked } });
                }
              }}
            />
          }
          label={t('device:energyMeter', { defaultValue: 'Energy Meter' })}
          className={classes.textFields}
        />
      </div>
      <WrapRequiredFieldsDescriptionComponent
        overrideTitle={t('device:energyMeterDescription', {
          defaultValue: 'Enable if the sensor is an energy meter.',
        })}
      />
    </div>
  );
};

const parseAnalogUnit = (range: Schema.AnalogInputRange | null | undefined): string => {
  const {
    NEGATIVE_FIVE_TO_FIVE_VOLTS,
    ZERO_TO_FIVE_VOLTS,
    ZERO_TO_TEN_VOLTS,
    NEGATIVE_TEN_TO_TEN_VOLTS,
    ZERO_TO_TWENTY_MILLIAMPS,
    ZERO_TO_TWENTYFOUR_MILLIAMPS,
    FOUR_TO_TWENTY_MILLIAMPS,
    NEGATIVE_TWENTYFIVE_TO_TWENTYFIVE_MILLIAMPS,
  } = Schema.AnalogInputRange;
  switch (range) {
    case ZERO_TO_FIVE_VOLTS:
    case ZERO_TO_TEN_VOLTS:
    case NEGATIVE_FIVE_TO_FIVE_VOLTS:
    case NEGATIVE_TEN_TO_TEN_VOLTS:
      return 'V';
    case ZERO_TO_TWENTY_MILLIAMPS:
    case ZERO_TO_TWENTYFOUR_MILLIAMPS:
    case FOUR_TO_TWENTY_MILLIAMPS:
    case NEGATIVE_TWENTYFIVE_TO_TWENTYFIVE_MILLIAMPS:
    default:
      return 'mA';
  }
};

interface MeasurementContentProps {
  handleChange: (newConfig: Partial<UpdateConfigInput>, meta?: { name?: string; description?: string }) => void;
  state: SensorSettingsState;
  invalidFormMessage: React.ReactNode | null;
}

const NumericField = ({
  value,
  onChange,
  significantDigits,
  ...props
}: {
  value: number;
  onChange: (value: number) => void;
  significantDigits?: number | null;
} & Omit<TextFieldProps, 'onChange' | 'value'>) => {
  const [textValue, setTextValue] = useState(value.toString());
  useEffect(() => {
    if (typeof significantDigits === 'number') {
      const factor = Math.pow(10, significantDigits);
      value *= factor;
      value = Math.round(value);
      value /= factor;
    }
    setTextValue(value.toString());
  }, [value]);
  return (
    <TextField
      {...props}
      value={textValue}
      onChange={(e) => {
        const { value } = e.target;
        const numeric = value === '-' || value === '.' ? 0 : +value;
        // We only wanna accept numbers as input
        if (isNaN(numeric) === false) {
          setTextValue(value);
          onChange(numeric);
        }
      }}
      inputProps={{
        inputMode: 'numeric',
        pattern: '^-?[0-9]*.?[0-9]*$',
      }}
    />
  );
};

const MeasurementContent: FC<MeasurementContentProps> = ({ state, handleChange, invalidFormMessage }) => {
  const [t, i18n] = useTranslation(['shared', 'unit', 'device']);
  const [sensorMin, sensorMax, multiplier] = useMemo(() => {
    const sensor = state.config?.sensor;
    if (!sensor) {
      return [0, 0, null];
    }
    if (typeof sensor.extraMultiplierForFrontend === 'number') {
      const data = recoverMinMaxWithMultiplier(
        sensor.dataMultiplier!,
        sensor.analogOffset!,
        sensor.analogInputRange!,
        sensor.extraMultiplierForFrontend,
      );
      return [data.sensorMin, data.sensorMax, sensor.extraMultiplierForFrontend];
    }
    const data = convertMultiplierOffset(sensor.dataMultiplier!, sensor.analogOffset!, sensor.analogInputRange!);
    return [data.sensorMin, data.sensorMax, null];
  }, [state.config?.sensor]);
  const { min: rangeMin, max: rangeMax } = parseAnalogRange(state?.config?.sensor?.analogInputRange!);

  const stringUnit = parseAnalogUnit(state?.config?.sensor?.analogInputRange);

  const onAnalogInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
    handleChange({ sensor: { analogInputRange: e.target.value as Schema.AnalogInputRange } });

  const onCalculationValueChange = ({
    min = sensorMin,
    max = sensorMax,
    mul,
  }: {
    min?: number;
    max?: number;
    mul?: number | null;
  }) => {
    if (mul === undefined) {
      mul = multiplier;
    }
    // We determine the encodning for the current min/max/multiplier and set that
    if (mul !== null) {
      const { dataMultiplier, analogOffset, multiplier } = convertSensorMinMaxAndMultiplier(
        min,
        max,
        state.config!.sensor!.analogInputRange!,
        mul,
      );
      handleChange({ sensor: { dataMultiplier, analogOffset, extraMultiplierForFrontend: multiplier } });
    } else {
      handleChange({
        sensor: {
          ...convertSensorMinMax(min, max, state.config!.sensor!.analogInputRange!),
          extraMultiplierForFrontend: null,
        },
      });
    }
  };

  const [selectedAnalogInputOrMultiplierState, setSelectedAnalogInputOrMultiplierState] = React.useState<
    'analog' | 'multiplier' | null
  >(() => {
    if (state?.config?.sensor?.analogInputRange) {
      return 'analog';
    } else if (state?.config?.sensor?.dataMultiplier) {
      return 'multiplier';
    } else {
      return null;
    }
  });

  const analogInputOrMultiplier = (
    <Grid item>
      <Grid container alignItems="center">
        <RadioGroup
          row
          name="row-radio-buttons-group"
          value={selectedAnalogInputOrMultiplierState?.toString()}
          onChange={(e) => {
            switch (e.target.value) {
              case 'analog':
                handleChange({ sensor: { dataMultiplier: null, analogOffset: 0 } });
                setSelectedAnalogInputOrMultiplierState('analog');
                break;
              case 'multiplier':
                handleChange({ sensor: { analogInputRange: null } });
                setSelectedAnalogInputOrMultiplierState('multiplier');
                break;
              default:
                console.error('unknown selection', e.target.value);
                break;
            }
          }}
        >
          <FormControlLabel
            value={'analog'}
            control={<Radio />}
            label={t('device:useAnalogInputRange', { defaultValue: 'Use analog input range' })}
          />
          <FormControlLabel
            value={'multiplier'}
            control={<Radio />}
            label={t('device:useDataMultiplier', { defaultValue: 'Scale input value' })}
          />
        </RadioGroup>
        <Grid item ml={1}>
          <Tooltip
            title={t('device:analogOrMultiplier', {
              defaultValue: `Use input range:
              Default method for reading analog sensors. This method maps the electric reading of the sensor to the actual measured property. Example: A temperature sensor outputs 4-20 mA which corresponds to -30 to 80 degrees celcius.
              Scale input value:
              Scales the raw reading sensor value by a number.`,
            })}
          >
            <HelpIcon color="action" />
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );

  const analogUnits = useMemo(() => translateAnalogExplanations(t), [i18n.language]);

  const analogInputRangeComponents = (
    <>
      <Grid item>
        <Grid container alignItems="center" wrap="nowrap">
          <SelectValidator
            name="type"
            label={`${t('device:analogRange', { defaultValue: 'Analog sensor range' })} *`}
            value={state?.config?.sensor?.analogInputRange || ''}
            validators={['required']}
            errorMessages={[t('shared:fieldIsRequired', { defaultValue: 'this field is required' })]}
            helperText={
              !state?.config?.sensor?.analogInputRange ? (
                <span style={{ color: red[500] }}>
                  {t('shared:fieldIsRequired', { defaultValue: 'this field is required' })}
                </span>
              ) : (
                ''
              )
            }
            onChange={onAnalogInputChange}
          >
            <MenuItem key="disabled" value={''} disabled>
              {t('device:selectAnalogRange', { defaultValue: 'Select analog sensor range' })}
            </MenuItem>
            {analogUnits.map((analogRange) => (
              <MenuItem
                style={{ paddingTop: '8px', paddingBottom: '8px' }}
                key={analogRange.key}
                value={analogRange.key}
              >
                <ListItemText primary={analogRange.name} />
              </MenuItem>
            ))}
          </SelectValidator>
          <Grid item ml={3}>
            <Tooltip
              title={t('device:selectAnalogRangeHint', {
                defaultValue:
                  'Insert the output range the sensor. This range is normally specified on the sensor itself. If using a DP2200 converter, use 4 to 20 mA',
              })}
            >
              <HelpIcon color="action" />
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container spacing={2} wrap="nowrap" alignItems="center">
          <Grid item xs={12} sm={6}>
            <NumericField
              significantDigits={5}
              name="sensorMinimum"
              label={t('device:sensorMinimumValue', {
                defaultValue: 'Sensor minimum ({{min}})',
                min: `${rangeMin} ${stringUnit}`,
              })}
              required
              fullWidth
              value={sensorMin}
              onChange={(min) => onCalculationValueChange({ min })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <NumericField
              significantDigits={5}
              name="sensorMaximum"
              label={t('device:sensorMaximumValue', {
                defaultValue: 'Sensor maximum ({{max}})',
                max: `${rangeMax} ${stringUnit}`,
              })}
              required
              fullWidth
              value={sensorMax}
              onChange={(max) => onCalculationValueChange({ max })}
            />
          </Grid>
          <Grid item ml={1}>
            <Tooltip
              title={t('device:minMaxTooltip', {
                defaultValue:
                  'Insert the expected value when the sensor is at the minimum and maximum reading. This is normally specified on the sensor itself',
              })}
            >
              <HelpIcon color="action" />
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={multiplier !== null}
              onChange={(_, checked) => onCalculationValueChange({ mul: checked ? 1 : null })}
            />
          }
          label={t('device:enableAnalogMultiplier', { defaultValue: 'Data multiplier' })}
        />
        <NumericField
          significantDigits={5}
          disabled={multiplier === null}
          value={multiplier ?? 1}
          onChange={(mul) => onCalculationValueChange({ mul: mul ?? 0 })}
        />
        <Grid item ml={3}>
          <Tooltip
            title={t('device:extraMultiplierTooltip', {
              defaultValue:
                'Multiplies the sensor reading by a specified value. Example: A power sensor outputs values 0-1000 W, but you want to see the value in kW. A multiplier is set to 0.001 which scales it from 0-1',
            })}
          >
            <HelpIcon color="action" />
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );

  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { analogOffset: 0, dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  const dataMultiplierComponent = (
    <Grid item>
      <InputValidator
        name="dataMultiplier"
        type="number"
        label={t('device:dataMultiplier', { defaultValue: 'Data multiplier' })}
        validators={['required', 'isPositive']}
        errorMessages={[
          t('shared:fieldIsRequired', { defaultValue: 'this field is required' }),
          t('shared:mustBePositive', { defaultValue: 'must be a positive number' }),
        ]}
        value={state?.config?.sensor?.dataMultiplier}
        helperText={
          !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0
            ? t('device:configurationDataMultiplierExplanation', {
                defaultValue:
                  'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
                dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
              })
            : undefined
        }
        onChange={onDataMultiplierChange}
      />
    </Grid>
  );

  return (
    <Grid container flexDirection="column" spacing={2}>
      <WiringSelection handleChange={handleChange} wiring={state.config?.sensor?.wiring || null} />
      {analogInputOrMultiplier}
      {selectedAnalogInputOrMultiplierState === 'analog' && analogInputRangeComponents}
      {selectedAnalogInputOrMultiplierState === 'multiplier' && dataMultiplierComponent}
      {invalidFormMessage}
      <br />
      <Grid item>
        <Typography sx={{ fontSize: 11, color: 'rgba(0,0,0,0.54)' }}>
          {t('shared:requiredFieldDesc', {
            defaultValue: 'All fields marked with an asterisk (*) are required.',
          })}
          {t('help:measurementSensorSetupStart', { defaultValue: 'For help with these settings check out or' })}{' '}
          <Link
            href="https://blackbirdhq.notion.site/Set-up-an-Analog-Sensor-b4f6ac8c5cdc4334ba0643d186581baf"
            target="_blank"
          >
            {t('shared:helpCenter', { defaultValue: 'Help Center' })}
          </Link>{' '}
          {t('help:measurementSensorSetupStart2', {
            defaultValue: 'for documentation.',
          })}
        </Typography>
        <Typography color="secondary"></Typography>
      </Grid>
    </Grid>
  );
};
